import { Button } from "../../ui/button";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/form";
import { Input } from "../../ui/input";
import { Alert, AlertTitle } from "../../ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { JunetEmbeddingSettings } from "@/types/EmbeddingSettings";
import { forwardRef, useImperativeHandle, useEffect } from "react";

type Props = {
    data?: JunetEmbeddingSettings;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

const JunetEmbeddingSettingsForm = forwardRef(
    (
        { data, initialData = {}, onDataChange, submitButton, readOnly, hideSubmitButton, error, onSubmit }: Props,
        ref
    ) => {
        const FormSchema = z.object({
            JUNET_API_KEY: z.string().min(1, "API key is required"),
        });

        const form = useForm<z.infer<typeof FormSchema>>({
            resolver: zodResolver(FormSchema),
            defaultValues: {
                JUNET_API_KEY: initialData?.JUNET_API_KEY || data?.JUNET_API_KEY || "",
            },
        });

        // Watch for changes and notify parent component
        useEffect(() => {
            const subscription = form.watch((value) => {
                if (onDataChange) {
                    onDataChange(value);
                }
            });

            return () => subscription.unsubscribe();
        }, [form, onDataChange]);

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                const result = await form.trigger();
                if (!result) {
                    // Get the first error message
                    const errors = form.formState.errors;
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                        console.error("Junet Embedding validation error:", firstError.message);
                    }
                }
                return result;
            },
            getFormData: () => {
                return form.getValues();
            },
            submitForm: () => {
                if (onSubmit) {
                    form.handleSubmit(onSubmit)();
                }
            },
        }));

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit((data) => onSubmit && onSubmit(data))}>
                    <div>
                        {error && (
                            <Alert variant="destructive" className="mb-2 dark:bg-red-800 dark:text-white">
                                <div className="flex items-center gap-2">
                                    <ExclamationTriangleIcon className="h-4 w-4" />
                                    <AlertTitle className="mb-0">{error}</AlertTitle>
                                </div>
                            </Alert>
                        )}
                        <div className="space-y-3">
                            <FormField
                                control={form.control}
                                name="JUNET_API_KEY"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>API Key</FormLabel>
                                        <FormControl>
                                            <Input {...field} readOnly={readOnly} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    {submitButton ? (
                        submitButton
                    ) : hideSubmitButton ? null : (
                        <div className="flex justify-end pt-10">
                            <Button type="submit">Save Changes</Button>
                        </div>
                    )}
                </form>
            </Form>
        );
    }
);

JunetEmbeddingSettingsForm.displayName = "JunetEmbeddingSettingsForm";

export default JunetEmbeddingSettingsForm;
